<template>
  <q-expansion-item
    v-model="expansion"
    class="cmp-coupon-list"
    dense
    switch-toggle-side
    :label="$t('summary.coupon_label')"
    header-class="expansion-header"
    :data-test="testNames.expansion"
    @hide="cleanForm"
  >
    <q-form ref="form" novalidate greedy @submit.prevent>
      <ws-input
        ref="input"
        v-model="code"
        path="cart.summary.discount"
        :label="$t('summary.coupon_input')"
        :rules="[]"
        :data-test="testNames.input"
      >
        <template v-slot:after>
          <ws-button
            submit
            :type="WsButtonEnum.Type.GHOST"
            :size="WsButtonEnum.Size.MEDIUM"
            :disable="loaders.submit"
            :loading="loaders.submit"
            :label="$t('summary.coupon_apply')"
            :data-test="testNames.applyButton"
            @click="submit"
          />
        </template>
      </ws-input>
      <div class="q-py-sm">
        <q-item v-for="item in items" :key="item.code" class="bonus">
          <q-item-section>
            <q-item-label class="text-positive text-left">
              <ws-icon :name="Icon.CIRCLE" class="q-mr-xs" />
              <small>
                <bonus-list class="inline" :bonuses="item.bonuses" />
              </small>
            </q-item-label>
            <q-item-label caption class="text-left q-ml-md q-pl-xs">
              {{
                item.code
              }}
            </q-item-label>
          </q-item-section>
          <q-item-section class="rm-section" side>
            <ws-button
              :type="WsButtonEnum.Type.DANGER"
              :loading="loaders[item.code]"
              :icon="Icon.CLOSE"
              :data-test="testNames.removeButton"
              @click="removeItem(item.code)"
            />
          </q-item-section>
        </q-item>
      </div>
      <ws-message path="cart.summary.discount_rm" type="alert" />
    </q-form>
  </q-expansion-item>
</template>

<script lang="ts">
import { Component, Vue, Watch } from 'vue-property-decorator';
import { mapState } from 'vuex';

import {
  QForm,
  QField,
  QItem,
  QItemSection,
  QItemLabel,
  QExpansionItem,
} from 'quasar';

import { get } from 'lodash-es';
import { validateForm, required } from '@/services/validation/validation.service.ts';
import { WsButton, WsButtonEnum, WsIcon, WsInput } from '@odin/components-vue2';
import WsMessage from './WsMessage.vue';

import type { Cart } from '@/types';
import { INVALID_FORM_ERROR } from '@/services/const';
import { applyCoupon, removeCoupon } from '@/services/cart/cart-api.service';
import { BonusList } from '@/components/BonusList.vue';

import { useMessages } from '@odin/services';
import { Icon } from '@odin/assets';

const {postMessage, handleUnstructuredError} = useMessages();

export const testNames = process.env.NODE_ENV === 'test' ? {
  expansion: 'coupon-expansion',
  input: 'coupon-input',
  applyButton: 'coupon-apply-btn',
  removeButton: 'coupon-remove-btn',
} : {} as Record<string, string>;

@Component({
  components: {
    QForm,
    QItem,
    QItemSection,
    QItemLabel,
    QExpansionItem,
    WsInput,
    WsIcon,
    WsButton,
    WsMessage,
    BonusList,
  },
  computed: {
    ...mapState(['cart']),
  },
})
export class CouponList extends Vue {
  testNames = testNames;
  readonly cart!: Cart;
  code: string = '';
  loaders: { [key: string]: boolean } = { submit: false };
  expansion = false;
  required = required;

  WsButtonEnum = WsButtonEnum;

  Icon = Icon;
  get items() {
    return get(this.cart, 'summary.coupons', []);
  }
  @Watch('items', { immediate: true })
  onItemsChanged(items: any[]) {
    if (items.length) {
      this.expansion = true;
    }
  }

  @Watch('code')
  onCodeChanged(code: string) {
    if (!code /*code was cleared */) {
      ((this.$refs.input as WsInput).cmp_vm.component.refs.qfield as QField).resetValidation();
    }
  }

  submit(event: SubmitEvent) {
    if (event && typeof event.preventDefault === 'function') {
      event.preventDefault();
    }
    if (this.loaders.submit || !this.code) {
      return;
    }
    this.loaders.submit = true;
    validateForm(this.$refs.form as Element & QForm)
      .then((result: boolean) => {
        if (!result) {
          return Promise.reject(INVALID_FORM_ERROR); // invalid new profile form
        }
        return applyCoupon(this.code).then(() => this.cleanForm());
      })
      .catch((e) => {
        postMessage('cart.summary.discount', this.$t(e.response.data.key));
      })
      .finally(() => (this.loaders.submit = false));
  }

  removeItem(code: string) {
    if (this.loaders[code]) {
      return;
    }
    Vue.set(this.loaders, code, true);
    removeCoupon(code)
      .catch((err) => handleUnstructuredError('cart.summary.discount_rm', err))
      .finally(() => (this.loaders[code] = false));
  }

  cleanForm() {
    this.code = '';
    ((this.$refs.input as WsInput).cmp_vm.component.refs.qfield as QField).blur();
    (this.$refs.form as QForm).resetValidation();
    // setTimeout because await this.$nextTick(); DOES NOT WORK
    setTimeout(() => {
      (this.$refs.form as QForm).resetValidation();
    }, 0);
  }
}
export default CouponList;
</script>

<style lang="scss" scoped>
.cmp-coupon-list {
  .ws-input ::v-deep.q-field__after {
    padding-left: map-get($space-sm, x);
  }

  .q-item {
    padding: 8px 0;

    .remove-item {
      position: relative;
      top: -(map-get($space-sm, y));
      right: -(map-get($space-md, x));
    }
  }
}

.q-expansion-item {
  ::v-deep {
    .expansion-header {
      padding: 0;

      &.q-hoverable:hover > .q-focus-helper {
        background: transparent;

        &:before,
        &:after {
          opacity: 0;
        }
      }

      > .q-item__section--avatar {
        padding: 0;
        min-width: 10px;

        > .q-expansion-item__toggle-icon {
          font-size: 18px;
          margin-right: 4px;
        }
      }

      > .q-item__section--main {
        text-align: left;
        font-weight: bold;
      }

      .q-item__label {
        font-weight: normal;
        font-size: 13px;
        line-height: 16px;
      }
    }
  }
}
.no-padded-control.q-field--borderless {
  height: 100%;
  margin: 0;

  ::v-deep .q-field__control {
    height: 100%;
    min-height: 100%;
    padding: 0;
    position: relative;
    right: -13px;

    > .q-field__control-container > button {
      border-radius: 0;
      background: $ws-base140;
      color: $ws-white;

      > .q-btn__wrapper {
        padding: 4px 8px;

        &:before {
          box-shadow: none;
        }
      }
    }
  }
}

.ws-field.q-field::v-deep
  > .q-field__inner
  > .q-field__bottom
  > .q-field__messages
  > div:first-of-type {
  margin-top: 5px;
}
</style>
